package services

data class EventRecord(
    val timestamp: String,
    val event: String,
    val detail: String,
    val best: String,
    val apecTotalRub: String,
    val eVisaTotalRub: String,
)

data class MetricsSummary(
    val views: Int = 0,
    val calculations: Int = 0,
    val codeClicks: Int = 0,
    val leads: Int = 0,
)

const val MetricsStorageKey = "apec_card_calculator_metrics_v2"

interface MetricsStore {
    fun read(): String
    fun write(value: String)
    fun clear()
}

class MetricsService(
    private val store: MetricsStore,
    private val nowIso: () -> String,
) {
    fun track(event: String, detail: String, best: String, apecTotalRub: String, eVisaTotalRub: String) {
        val record = EventRecord(
            timestamp = nowIso(),
            event = event,
            detail = detail.sanitizeMetric(),
            best = best.sanitizeMetric(),
            apecTotalRub = apecTotalRub,
            eVisaTotalRub = eVisaTotalRub,
        )
        val current = store.read()
        store.write(if (current.isBlank()) encodeEventRecord(record) else "$current\n${encodeEventRecord(record)}")
    }

    fun summary(): MetricsSummary = summarizeEvents(decodeEventRecords(store.read()))

    fun exportCsv(): String {
        val header = "timestamp,event,detail,best,apec_total_rub,evisa_total_rub"
        val rows = decodeEventRecords(store.read()).map { record ->
            listOf(record.timestamp, record.event, record.detail, record.best, record.apecTotalRub, record.eVisaTotalRub)
                .joinToString(",") { it.csvCell() }
        }
        return (listOf(header) + rows).joinToString("\n")
    }

    fun clear() = store.clear()
}

fun encodeEventRecord(record: EventRecord): String {
    return listOf(record.timestamp, record.event, record.detail, record.best, record.apecTotalRub, record.eVisaTotalRub)
        .joinToString("|") { it.sanitizeMetric() }
}

fun decodeEventRecords(raw: String): List<EventRecord> {
    return raw.lines()
        .filter { it.isNotBlank() }
        .map { line ->
            val parts = (line.split("|") + List(6) { "" }).take(6)
            EventRecord(parts[0], parts[1], parts[2], parts[3], parts[4], parts[5])
        }
}

fun summarizeEvents(events: List<EventRecord>): MetricsSummary {
    return MetricsSummary(
        views = events.count { it.event == "page_view" },
        calculations = events.count { it.event == "calculation_save" },
        codeClicks = events.count { it.event == "referral_code_click" },
        leads = events.count { it.event == "lead_submit" },
    )
}

fun String.sanitizeMetric(): String {
    return replace("|", "/")
        .replace("\n", " ")
        .replace("\r", " ")
        .trim()
}

fun String.csvCell(): String {
    val safeValue = if (trimStart().startsWithSpreadsheetFormulaPrefix()) "'$this" else this
    val escaped = safeValue.replace("\"", "\"\"")
    return "\"$escaped\""
}

private fun String.startsWithSpreadsheetFormulaPrefix(): Boolean {
    return when (firstOrNull()) {
        '=', '+', '-', '@' -> true
        else -> false
    }
}
